/**
 * Admin login: phone + OTP (register then verify-otp).
 * Design System: surface tokens, rounded-card, InputField, Button, full RTL + i18n.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@contexts/AuthContext';
import { authRegister, authVerifyOtp } from '@services/api';
import type { ApiError } from '@services/api';
import { SaaeLogo } from '@components/SaaeLogo';
import { Button } from '@components/Button/Button';
import { InputField } from '@components/Input';
import { useLanguage } from '@hooks/useLanguage';
import { BACKOFFICE_BASE } from '@lib/backofficeRoutes';

const DEFAULT_PHONE = '500000000';
const DEFAULT_CODE = '123456';

export function AdminLoginPage() {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const { setSessionToken, refreshAuth } = useAuth();
  const [phone, setPhone] = useState(DEFAULT_PHONE);
  const [code, setCode] = useState(DEFAULT_CODE);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isRtl = language === 'ar';

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await authRegister(phone);
      const data = await authVerifyOtp(phone, code);
      setSessionToken(data.token ?? null, true, {
        profileCompleted: true,
      });
      await refreshAuth();
      navigate(BACKOFFICE_BASE, { replace: true });
    } catch (err) {
      const ax = err as {
        response?: { data?: { error?: { message?: string }; message?: string } };
        message?: string;
      };
      const msg =
        ax?.response?.data?.error?.message ??
        (err as ApiError)?.message ??
        ax?.message ??
        t('admin.login.errorGeneric', { defaultValue: 'Sign-in failed. Check the number and code.' });
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      className="flex min-h-screen flex-col items-center justify-center bg-surface-web px-4 py-10 font-sans"
      dir={isRtl ? 'rtl' : 'ltr'}
    >
      <div className="mb-8 flex justify-center">
        <SaaeLogo size="small" mode="light" className="!h-9" />
      </div>
      <div className="w-full max-w-md rounded-card border border-surface-border bg-surface-cards p-6 shadow-sm sm:p-8">
        <h1 className="mb-1 text-center text-xl font-bold text-primary font-sans sm:text-2xl">
          {t('admin.login.title', { defaultValue: 'Admin sign-in' })}
        </h1>
        <p className="mb-6 text-center text-sm text-primary/60 font-sans">
          {t('admin.login.subtitle', { defaultValue: 'Enter your mobile number and verification code.' })}
        </p>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <InputField
            className="force-ltr"
            label={t('admin.login.phoneLabel', { defaultValue: 'Mobile (9 digits, starts with 5)' })}
            type="text"
            value={phone}
            onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 9))}
            placeholder="5XXXXXXXX"
            size="lg"
            fullWidth
            contentDir="ltr"
            autoComplete="tel"
            state="default"
          />
          <InputField
            label={t('admin.login.codeLabel', { defaultValue: 'Verification code (6 digits)' })}
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="123456"
            size="lg"
            fullWidth
            contentDir="ltr"
            maxLength={6}
            autoComplete="one-time-code"
            state="default"
          />
          {error && (
            <p className="rounded-[16px] border border-surface-error-primary bg-surface-error-lighter/50 px-4 py-3 text-sm text-surface-error-primary font-sans" role="alert">
              {error}
            </p>
          )}
          <Button type="submit" variant="primary" size="lg" fullWidth disabled={loading || !phone || code.length !== 6} loading={loading} data-testid="login-submit-btn">
            {t('admin.login.submit', { defaultValue: 'Sign in' })}
          </Button>
        </form>
        <p className="mt-6 text-center text-xs text-primary/50 font-sans">
          {t('admin.login.devHint', {
            defaultValue: 'Development: use 500000000 and 123456 or the seed OTP.',
          })}
        </p>
      </div>
    </div>
  );
}
